/**
 * 幕二 · 时间轴带(2026-08-06 幕二重设计,变体 B 的核心收益)。
 *
 * ═══ 它补的是网格补不了的东西 ═══════════════════════════════════════════════
 * 分镜网格里 4s 的镜和 15s 的镜长得一样大 —— 而**节奏**恰恰是成片好坏的关键,
 * 现在界面上完全不可见。这条带按秒等比,镜长差异一眼看出。
 *
 * ═══ ★更要紧的:它把「段」画出来了 ═══════════════════════════════════════════
 * 发给视频模型的 prompt 是**按段**的(相邻镜合 1~3 镜一段,一段一条),而段界还决定
 * 引擎路由 / 参考禁混 / 15.2s 参考墙 —— 用户此前完全看不到这个结构。
 * 段来自 `useCompilePreview` 的真回包(ai `chunk_shots` 的产物),不是前端猜的:
 * 前端另分一套就是第二份判据,必漂移。
 *
 * 拍法节拍(蓝图路)与段是**两回事**,各画一条:节拍是"怎么讲",段是"怎么发给模型"。
 * 合成一条会让人以为一拍就是一段。
 */
import type { VideoPromptSegmentDto } from "../../api/videoService";
import { VOICEOVER_DENSITY_MAX, voiceoverDensity } from "@/shared/lib/video/cameraLexicon";
import { cn } from "@/shared/lib/utils";
import type { PanelShotData } from "../../model/panelShot";
import { ARGUMENT_ARC_TINT, argumentArcSecRuns } from "../../model/argumentArc";

interface Props {
  shots: PanelShotData[];
  /** 真编译回来的段(空 = 还没拿到/零分镜,则不画段条)。 */
  segments: VideoPromptSegmentDto[];
  selected: number | null;
  onSelect: (index: number) => void;
  /** 每镜所属节拍名(蓝图路);缺省不画节拍条。 */
  beatLabels?: (string | null)[];
  /**
   * W4(2026-09-13):导演产的逐镜叙事角色(hook/build_tension/turn/resolution/cta,原始枚举
   * 值,与 `beatLabels` 那套已翻译文案是两回事)。缺省/空 → 论证弧色带完全不渲染(零现状回归)。
   */
  narrativeRoles?: (string | null)[];
}

/** 把逐镜节拍名压成连续区段(相邻同名合一段),用于画节拍条。 */
function beatRuns(labels: (string | null)[], shots: PanelShotData[]) {
  const runs: { label: string; sec: number }[] = [];
  labels.forEach((l, i) => {
    const name = (l ?? "").trim();
    if (!name) return;
    const last = runs[runs.length - 1];
    if (last && last.label === name) last.sec += shots[i]?.duration ?? 0;
    else runs.push({ label: name, sec: shots[i]?.duration ?? 0 });
  });
  return runs;
}

const SEG_TINTS = [
  "hsl(var(--primary) / .5)",
  "hsl(270 80% 65% / .5)",
  "hsl(38 95% 60% / .45)",
  "hsl(160 70% 50% / .45)",
];

export function VideoTimelineBand({
  shots,
  segments,
  selected,
  onSelect,
  beatLabels,
  narrativeRoles,
}: Props) {
  const total = shots.reduce((a, s) => a + (s.duration || 0), 0);
  if (shots.length === 0 || total <= 0) return null;
  const runs = beatLabels ? beatRuns(beatLabels, shots) : [];
  const arcRuns = narrativeRoles
    ? argumentArcSecRuns(
        narrativeRoles,
        shots.map((s) => s.duration)
      )
    : [];

  return (
    <div
      data-video-timeline-band
      className="rounded-[10px] border border-border/60 bg-card/40 px-3 py-2.5"
    >
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-2xs text-muted-foreground">
        <span>
          总长 <span className="font-mono text-foreground">{total}s</span> · {shots.length} 镜
          {segments.length > 0 && <> · 发给模型 {segments.length} 段</>}
        </span>
        <span>按秒等比 · 点任一段选中该镜</span>
      </div>

      {/* 镜条:宽度 ∝ 时长 */}
      <div className="flex h-11 gap-[3px]">
        {shots.map((s, i) => {
          const over = voiceoverDensity(s.voiceover, s.duration) > VOICEOVER_DENSITY_MAX;
          return (
            <button
              key={i}
              type="button"
              aria-pressed={selected === i}
              title={`镜 ${i + 1} · ${s.duration}s`}
              data-testid={`timeline-shot-${i}`}
              onClick={() => onSelect(i)}
              style={{ flexGrow: s.duration || 1, flexBasis: 0 }}
              className={cn(
                "relative flex min-w-0 cursor-pointer items-end overflow-hidden rounded-[5px] border px-1.5 py-1 text-left transition-colors",
                "bg-gradient-to-br from-slate-700/40 to-slate-900/60",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                selected === i
                  ? "border-primary shadow-[0_0_0_1px_hsl(var(--primary))]"
                  : "border-border/60 hover:border-primary/60"
              )}
            >
              <span className="truncate text-2xs text-foreground/90">镜{i + 1}</span>
              {s.heroMoment && (
                <span className="absolute left-1 top-0.5 text-2xs text-amber-500">★</span>
              )}
              {over && (
                <span
                  title="口播超密"
                  className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-amber-500"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* 段条:真 prompt 的边界(一段一条 prompt)。与节拍条分开画 —— 两回事。 */}
      {segments.length > 0 && (
        <div className="mt-1 flex gap-[3px]">
          {segments.map((sg, i) => (
            <div
              key={sg.seq}
              title={`第 ${sg.seq} 段 · 镜 ${sg.shotIndexes.map((x: number) => x + 1).join("、")} · ${sg.chars} 字`}
              style={{
                flexGrow: sg.durationSec || 1,
                flexBasis: 0,
                background: SEG_TINTS[i % SEG_TINTS.length],
              }}
              className={cn(
                "truncate rounded-[4px] px-1 py-px text-center text-2xs font-medium",
                sg.overBudget ? "text-amber-950 ring-1 ring-amber-500" : "text-slate-950"
              )}
            >
              第{sg.seq}段{sg.overBudget ? " · 超字数" : ""}
            </div>
          ))}
        </div>
      )}

      {/* 论证弧色带(W4):narrativeRoles 连续段按秒聚合,与段条/节拍条视觉区分(浅底深字)。 */}
      {arcRuns.length > 0 && (
        <div className="mt-1 flex gap-[3px]" data-testid="video-timeline-arc-band">
          {arcRuns.map((r, i) => (
            <div
              key={i}
              title={`${r.label} · ${r.sec}s`}
              style={{ flexGrow: r.sec || 1, flexBasis: 0 }}
              className={cn(
                "truncate rounded-[4px] border px-1 py-px text-center text-2xs font-medium",
                ARGUMENT_ARC_TINT[r.role] ?? "border-border/50 text-muted-foreground"
              )}
            >
              {r.label}
            </div>
          ))}
        </div>
      )}

      {/* 节拍条(蓝图路):这条片"怎么讲"。 */}
      {runs.length > 0 && (
        <div className="mt-1 flex gap-[3px]">
          {runs.map((r, i) => (
            <div
              key={i}
              style={{ flexGrow: r.sec || 1, flexBasis: 0 }}
              className="truncate rounded-[4px] border border-border/50 px-1 py-px text-center text-2xs text-muted-foreground"
            >
              {r.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
